import React from 'react'
import { MDXTag } from '@mdx-js/tag'
import { Head, Notes, Image, Appear, components as C, Split } from "mdx-deck";
import { CodeSurfer } from "mdx-deck-code-surfer";
export { default as theme } from "./theme";
export { components } from "mdx-deck-code-surfer";
export const meta = {}

export default [
  ({ components, ...props }) => <MDXTag name="wrapper"  components={components}>

<Head>
  <title>Async Generators</title>
</Head>
<MDXTag name="h1" components={components}>{`JavaScript Generators`}</MDXTag></MDXTag>,

({ components, ...props }) => <MDXTag name="wrapper"  components={components}><MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{"className":"language-typescript","metaString":"JavaScript Generators","JavaScript":true,"Generators":true}}>{`function* agenda() {
  yield "obligatory introduction";
  yield "new syntax";
  yield "basics";
  yield "asynchronous behaviour";
  yield "flow control from the caller side";
  yield "generator use-case: early implementation of async/await";
  yield "generator use-case: sagas";
}
`}</MDXTag></MDXTag></MDXTag>,

({ components, ...props }) => <MDXTag name="wrapper"  components={components}><MDXTag name="h1" components={components}>{`About me`}</MDXTag>
<C.ul>
  <C.li>
    Lenz Weber (<em>@phry</em> on Twitter, <em>phryneas</em> everywhere else)
  </C.li>
  <C.li>
    Developer at
    <img
      src="./assets/mayflower_logo_transparent.png"
      lang="typescript"
      style={{
        height: "1.3em",
        verticalAlign: "bottom",
        position: "relative",
        top: "5px",
        left: "-5px"
      }}
    />
  </C.li>
  <C.li>Working in professional web development since 15 years</C.li>
  <C.li>In love with the Web, Open Source, Linux and Security</C.li>
  <C.li>Just getting used to conference speaking, so bear with me</C.li>
</C.ul></MDXTag>,

({ components, ...props }) => <MDXTag name="wrapper"  components={components}><MDXTag name="h1" components={components}>{`Let's get to know Generators`}</MDXTag></MDXTag>,

({ components, ...props }) => <MDXTag name="wrapper"  components={components}><MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{"className":"language-typescript","metaString":"Syntax Basics","Syntax":true,"Basics":true}}>{`function* myFirstGenerator() {
  yield "some-return-value";
  yield "another-return-value";
}

const myGeneratorInstance = myFirstGenerator();
----
*
1[1] > the asterisk marks a function as Generator Function
2[1],3[1] > use yield multiple times to return multiple values
6[5:7] > call like a function to get your Generator Object
`}</MDXTag></MDXTag>
<MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{"className":"language-notes","metaString":""}}>{`some highlights
`}</MDXTag></MDXTag></MDXTag>,

({ components, ...props }) => <MDXTag name="wrapper"  components={components}><MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{"className":"language-typescript","metaString":"Usage Basics: the for-of-loop","Usage":true,"Basics:":true,"the":true,"for-of-loop":true}}>{`function* myFirstGenerator() {
  yield "some-return-value";
  yield "another-return-value";
  return "then-we-really-return";
}

for (const value of myFirstGenerator()) {
  console.log(value);
}
`}</MDXTag></MDXTag>
<MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{"className":"language-notes","metaString":""}}>{`no steps
`}</MDXTag></MDXTag></MDXTag>,

({ components, ...props }) => <MDXTag name="wrapper"  components={components}><MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{"className":"language-typescript","metaString":"Usage Basics: using the Iterable protocol","Usage":true,"Basics:":true,"using":true,"the":true,"Iterable":true,"protocol":true}}>{`function* myFirstGenerator() {
  yield "some-return-value";
  yield "another-return-value";
  return "then-we-really-return";
}

const iterableObject = myFirstGenerator();

let nextValue = iterableObject.next();
while (!nextValue.done) {
  console.log(nextValue.value);

  nextValue = iterableObject.next();
}
`}</MDXTag></MDXTag>
<MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{"className":"language-notes","metaString":""}}>{`no steps
`}</MDXTag></MDXTag></MDXTag>,

({ components, ...props }) => <MDXTag name="wrapper"  components={components}><MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{"className":"language-typescript","metaString":"Asynchronous Behaviour: yield steps out of the Generator","Asynchronous":true,"Behaviour:":true,"yield":true,"steps":true,"out":true,"of":true,"the":true,"Generator":true}}>{`function* myGenerator() {
  console.log("generator started");
  yield "some-yielded-value";

  console.log("continuing");
  yield "another-yielded-value";

  console.log("continuing");
  return "then-we-really-return";
}

let nextValue;
const iterableObject = myGenerator();
console.log("generator created");
nextValue = iterableObject.next();
console.log("got first yielded value", nextValue);
nextValue = iterableObject.next();
console.log("got second yielded value", nextValue);
nextValue = iterableObject.next();
console.log("got third value", nextValue);
----
*
13[4:7] > // calling myGenerator()
14 > generator created
15[2:6] > // calling first next()
2 > generator started
3 > // yielding first value
16 > got first yielded value { done: false, value: "some-yielded-value" }
17[2:6] > // calling second next()
5 > continuing
6 > // yielding second value
18 > got second return value { done: false, value: "another-yielded-value" }
19[2:6] > // calling third next()
8 > continuing
9 > // returning a value
20 > got third value { done: true, value: "then-we-really-return" }
`}</MDXTag></MDXTag>
<MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{"className":"language-notes","metaString":""}}>{`steps through
`}</MDXTag></MDXTag></MDXTag>,

({ components, ...props }) => <MDXTag name="wrapper"  components={components}><MDXTag name="h1" components={components}>{`Flow Control from the Caller`}</MDXTag></MDXTag>,

({ components, ...props }) => <MDXTag name="wrapper"  components={components}><MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{"className":"language-typescript","metaString":"Flow Control from the Caller: passing values into a running Generator Function","Flow":true,"Control":true,"from":true,"the":true,"Caller:":true,"passing":true,"values":true,"into":true,"a":true,"running":true,"Generator":true,"Function":true}}>{`function* sumGenerator() {
  let sum = 0;
  while (true) {
    const value = yield sum;
    console.log("got passed", value);
    sum += value;
  }
}

const iterableObject = sumGenerator();
for (const add of [1, 2, 3]) {
  const returnedValue = iterableObject.next(add).value;
  console.log("returned value is", returnedValue);
}
`}</MDXTag></MDXTag>
<MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{"className":"language-notes","metaString":""}}>{`no steps
`}</MDXTag></MDXTag></MDXTag>,

({ components, ...props }) => <MDXTag name="wrapper"  components={components}><MDXTag name="h2" components={components}>{`The chicken-egg-problem of passing values in both directions`}</MDXTag>
<MDXTag name="p" components={components}>{`If we pass in a value to be used before the first `}<MDXTag name="inlineCode" components={components} parentName="p">{`yield`}</MDXTag>{` and at the same time use the `}<MDXTag name="inlineCode" components={components} parentName="p">{`yield`}</MDXTag>{` keyword to access values passed in, we can never access the first value that is passed in.`}</MDXTag>
<MDXTag name="blockquote" components={components}>
<MDXTag name="p" components={components} parentName="blockquote">{`⇒ The first time you call
`}<MDXTag name="inlineCode" components={components} parentName="p">{`Iterator.prototype.next(value)`}</MDXTag><MDXTag name="br" components={components} parentName="p"></MDXTag>
{``}<MDXTag name="inlineCode" components={components} parentName="p">{`value`}</MDXTag>{` goes straight to `}<MDXTag name="inlineCode" components={components} parentName="p">{`/dev/null`}</MDXTag>{`!`}</MDXTag>
</MDXTag></MDXTag>,

({ components, ...props }) => <MDXTag name="wrapper"  components={components}><MDXTag name="h2" components={components}>{`Possile solutions:`}</MDXTag>
<MDXTag name="ul" components={components}>
<MDXTag name="li" components={components} parentName="ul"><MDXTag name="strong" components={components} parentName="li">{`invert the problem`}</MDXTag>{`: call `}<MDXTag name="inlineCode" components={components} parentName="li">{`next`}</MDXTag>{` once just to get the generator started, without using the return value.`}</MDXTag>
<MDXTag name="li" components={components} parentName="ul"><MDXTag name="strong" components={components} parentName="li">{`don't pass the first value to `}<MDXTag name="inlineCode" components={components} parentName="strong">{`next`}</MDXTag></MDXTag>{`, but as an argument to the Generator Function call itself.`}</MDXTag>
</MDXTag>
<MDXTag name="h3" components={components}>{`Both solutions require the user of your generator to use it accordingly, so write some documentation.`}</MDXTag></MDXTag>,

({ components, ...props }) => <MDXTag name="wrapper"  components={components}><MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{"className":"language-typescript","metaString":"Example for the \"inverted\" solution","Example":true,"for":true,"the":true,"\"inverted\"":true,"solution":true}}>{`function* sumGenerator() {
  let sum = 0;
  while (true) {
    const value = yield sum;
    console.log("got passed", value);
    sum += value;
  }
}

const iterableObject = sumGenerator();
iterableObject.next();
for (const add of [1, 2, 3]) {
  const returnedValue = iterableObject.next(add).value;
  console.log("returned value is", returnedValue);
}
----
*
10 > create the generator
11 > start the generator
2
4[5:7] > yield 0;
12 > const add = 1;
13[4:9] > next(1);
4[1:4] > got 1
5 > conosle.log("got passed 1")
6 > sum = 0 + 1;
4[5:7] > yield 1;
13[1:3,10:12] > { done: false, value: 1 }
14 > console.log("returned value is", 1)
12 > const add = 2; // et cetera
`}</MDXTag></MDXTag>
<MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{"className":"language-notes","metaString":""}}>{`has steps
`}</MDXTag></MDXTag></MDXTag>,

({ components, ...props }) => <MDXTag name="wrapper"  components={components}><MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{"className":"language-typescript","metaString":"Example for the \"function argument\" solution","Example":true,"for":true,"the":true,"\"function":true,"argument\"":true,"solution":true}}>{`function* sumGenerator(firstPassedValue) {
  let sum = firstPassedValue;
  while (true) {
    const value = yield sum;
    console.log("got passed", value);
    sum += value;
  }
}

const iterableObject = sumGenerator(1);
for (const add of [undefined, 2, 3]) {
  const returnedValue = iterableObject.next(add).value;
  console.log("returned value is", returnedValue);
}
----
*
1[5],2,10[7],11[9] > ''
`}</MDXTag></MDXTag>
<MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{"className":"language-notes","metaString":""}}>{`just highlights differences
`}</MDXTag></MDXTag></MDXTag>,

({ components, ...props }) => <MDXTag name="wrapper"  components={components}><MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{"className":"language-typescript","metaString":"Flow Control from the Caller: injecting an Error","Flow":true,"Control":true,"from":true,"the":true,"Caller:":true,"injecting":true,"an":true,"Error":true}}>{`function* generateFib() {
  let a = 0,
    b = 1;
  try {
    while (true) {
      yield a;
      const _ = a + b;
      a = b;
      b = _;
    }
  } catch (e) {
    console.log("guess I went too far.");
  }
}

const iterableFib = generateFib();
for (const fib of iterableFib) {
  console.log(fib);
  if (fib > 100) {
    iterableFib.throw("stop this nonsense!");
  }
}
`}</MDXTag></MDXTag>
<MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{"className":"language-notes","metaString":""}}>{`no steps
`}</MDXTag></MDXTag></MDXTag>,

({ components, ...props }) => <MDXTag name="wrapper"  components={components}><MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{"className":"language-typescript","metaString":"Flow Control from the Caller: forcing to return","Flow":true,"Control":true,"from":true,"the":true,"Caller:":true,"forcing":true,"to":true,"return":true}}>{`function* generateFib() {
  let a = 0,
    b = 1;
  try {
    while (true) {
      try {
        yield a;
      } catch (e) {
        console.log("you will never stop me with", e);
      }
      const _ = a + b;
      a = b;
      b = _;
    }
  } finally {
    console.log("oh, you got me nonetheless.");
  }
}

const iterableFib = generateFib();
for (const fib of iterableFib) {
  console.log(fib);
  iterableFib.throw("stop.");
  if (fib > 100) {
    iterableFib.return("stop this nonsense!");
    // returns { done: true, value: "stop this nonsense!" }
  }
}
`}</MDXTag></MDXTag>
<MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{"className":"language-notes","metaString":""}}>{`no steps
`}</MDXTag></MDXTag></MDXTag>,

({ components, ...props }) => <MDXTag name="wrapper"  components={components}><MDXTag name="h2" components={components}>{`As a sidenote`}</MDXTag>
<MDXTag name="ul" components={components}>
<MDXTag name="li" components={components} parentName="ul">{`you can override the return value that is forced by `}<MDXTag name="inlineCode" components={components} parentName="li">{`myIterator.return("forced-return-value")`}</MDXTag>{` `}<br/>{` by returning something different in your `}<MDXTag name="inlineCode" components={components} parentName="li">{`finally`}</MDXTag>{` block`}</MDXTag>
<MDXTag name="li" components={components} parentName="ul">{`with `}<MDXTag name="inlineCode" components={components} parentName="li">{`yield*`}</MDXTag>{`, you can delegate to another generator (stuff like "throw" will control the inner generator until it is done!)`}</MDXTag>
</MDXTag></MDXTag>,

({ components, ...props }) => <MDXTag name="wrapper"  components={components}><MDXTag name="h1" components={components}>{`Let's look at real-world use cases`}</MDXTag>
<MDXTag name="h2" components={components}>{`Use Case: simulating async-await-like behaviour`}</MDXTag></MDXTag>,

({ components, ...props }) => <MDXTag name="wrapper"  components={components}><MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{"className":"language-typescript","metaString":"what is async-await?","what":true,"is":true,"async-await?":true}}>{`// before async/await
function fetchMovieTitles() {
  return fetch("http://example.com/movies.json")
    .then(function(response) {
      return response.json();
    })
    .then(function(json) {
      return json.map(movie => movie.title);
    });
}
const movieTitlePromise = fetchMovieTitles();

// with async await
async function fetchMovieTitles() {
  const response = await fetch("http://example.com/movies.json");
  const json = await response.json();
  return json.map(movie => movie.title);
}
const movieTitlePromise = fetchMovieTitles();
`}</MDXTag></MDXTag>
<MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{"className":"language-notes","metaString":""}}>{`no steps
imperative
`}</MDXTag></MDXTag></MDXTag>,

({ components, ...props }) => <MDXTag name="wrapper"  components={components}><MDXTag name="ul" components={components}>
<MDXTag name="li" components={components} parentName="ul">{`async-await was part of ES2017 `}<br/>{` landed in Chrome 55, Firefox 52, Edge 15 (2016/2017)`}</MDXTag>
<MDXTag name="li" components={components} parentName="ul">{`generators were already part of ES2015 `}<br/>{` available since Chrome 39, Firefox 26, Edge 13 (2013-2015)`}</MDXTag>
<MDXTag name="li" components={components} parentName="ul">{`in the meantime, people got creative`}</MDXTag>
<MDXTag name="li" components={components} parentName="ul">{`so what did that look like?`}</MDXTag>
</MDXTag></MDXTag>,

({ components, ...props }) => <MDXTag name="wrapper"  components={components}><MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{"className":"language-typescript","metaString":"How we want to use it","How":true,"we":true,"want":true,"to":true,"use":true,"it":true}}>{`// with async await
async function fetchMovieTitles() {
  const response = await fetch("https://example.com/movies.json");
  const json = await response.json();
  return json.map(movie => movie.title);
}
const movieTitlePromise = fetchMovieTitles();

// with our solution
function* fetchMovieTitles() {
  const response = yield fetch("https://example.com/movies.json");
  const json = yield response.json();
  return json.map(movie => movie.title);
}

const movieTitlePromise = runAsync(fetchMovieTitles());

function runAsync(generator) {
  function run(arg) {
    const nextValue = generator.next(arg);

    if (nextValue.done) {
      return nextValue.value;
    }
    return Promise.resolve(nextValue.value).then(run);
  }

  return run();
}
----
1:7
9:16
18:29
`}</MDXTag></MDXTag>
<MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{"className":"language-notes","metaString":""}}>{`highlight parts
orchestrating function
`}</MDXTag></MDXTag></MDXTag>,

({ components, ...props }) => <MDXTag name="wrapper"  components={components}><MDXTag name="h1" components={components}>{`Let's look at real-world use cases`}</MDXTag>
<MDXTag name="h2" components={components}>{`Use Case: Sagas`}</MDXTag></MDXTag>,

({ components, ...props }) => <MDXTag name="wrapper"  components={components}><MDXTag name="h1" components={components}>{`Sagas`}</MDXTag>
<MDXTag name="ul" components={components}>
<MDXTag name="li" components={components} parentName="ul">{`are triggered from asynchronous actions`}</MDXTag>
<MDXTag name="li" components={components} parentName="ul">{`call "Effects" in sequence`}</MDXTag>
<MDXTag name="li" components={components} parentName="ul">{`an Effect is the description of how a synchronous or asynchronous action should be executed `}<br/>{` (other sagas can be called using Effects, too!)`}</MDXTag>
<MDXTag name="li" components={components} parentName="ul">{`the real execution of Effects is done by the runtime`}</MDXTag>
<MDXTag name="li" components={components} parentName="ul">{`can be cancelled from the outside `}<br/>{` (for example if we want a saga to run only once, but a newer asynchronous actions re-starts the saga)`}</MDXTag>
<MDXTag name="li" components={components} parentName="ul">{`can "clean up" after themselves if they are cancelled`}</MDXTag>
</MDXTag>
<MDXTag name="h4" components={components}>{`Note: Sagas are available for many different platforms, we will look at redux-saga here`}</MDXTag>
<MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{"className":"language-notes","metaString":""}}>{`effects are commands to the orchestrator
`}</MDXTag></MDXTag></MDXTag>,

({ components, ...props }) => <MDXTag name="wrapper"  components={components}><MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{"className":"language-typescript","metaString":"Saga example: AutoComplete in redux-saga","Saga":true,"example:":true,"AutoComplete":true,"in":true,"redux-saga":true}}>{`import { takeLatest, put, call, race, cancelled,delay } from "redux-saga/effects";

const api = "//api.example.com/";

export default function* autoCompleteSaga() {
  yield takeLatest("autoComplete/keyDown", updateAutoComplete);
}

function* updateAutoComplete(action) {
  try {
    const { results, timeout } = yield race({
      results: call(getData, \`\${api}/cityNames/\${action.payload.inputValue}\`),
      timeout: call(delay, 10000)
    });

    if (timeout) {
      throw new Error("timed out after 10 seconds");
    }

    yield put({ type: "autoComplete/setNewValues", payload: results });
  } catch (e) {
    yield put({ type: "autoComplete/fetchFailed", e });
  }
}

function* getData(url) {
  const response = yield call(fetchData, url);
  if (response.status === 200) {
    const json = yield call([response, response.json]);
    return json;
  } else {
    throw new Error("bad return code");
  }
}

function* fetchData(url) {
  const abortController = new AbortController();
  try {
    const result = yield call(fetch, url, { signal: abortController.signal });
    return result;
  } finally {
    if (yield cancelled()) {
      abortController.abort();
    }
  }
}
----
*
1
5:7
9:24
26:34
36:46
*
`}</MDXTag></MDXTag>
<MDXTag name="pre" components={components}><MDXTag name="code" components={components} parentName="pre" props={{"className":"language-notes","metaString":""}}>{`highlight parts
`}</MDXTag></MDXTag></MDXTag>,

({ components, ...props }) => <MDXTag name="wrapper"  components={components}><MDXTag name="h1" components={components}>{`Time for Questions & Discussion`}</MDXTag></MDXTag>,

({ components, ...props }) => <MDXTag name="wrapper"  components={components}><MDXTag name="h1" components={components}>{`By the way...`}</MDXTag>
<MDXTag name="h1" components={components}>{`We are hiring!`}</MDXTag>
<img
  src="./assets/mayflower_logo_transparent.png"
  style={{ maxWidth: "50%" }}
/>
<MDXTag name="p" components={components}>{`(talk to me)`}</MDXTag></MDXTag>
]